import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { startOfWeekMon, isoDate, addDays, formatTimeDisplay } from "@/lib/schedule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RescheduleAssessmentModal — move a CONFIRMED assessment to a new slot.
 * Same 2-week day-tab slot picker as the Confirm modal, but POSTs to
 * /coach/assessment-requests/{id}/reschedule (cancels old booking, books
 * new, emails the parent the new time).
 */
export default function RescheduleAssessmentModal({ requestId, parentName, currentLabel, onClose, onDone }) {
    const [weekStart, setWeekStart] = useState(() => startOfWeekMon(new Date()));
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(true);
    const [chosenSlot, setChosenSlot] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [activeDay, setActiveDay] = useState(null);

    const weekFrom = useMemo(() => isoDate(weekStart), [weekStart]);
    const weekTo = useMemo(() => isoDate(addDays(weekStart, 13)), [weekStart]);

    const dayTabs = useMemo(() => {
        const groups = new Map();
        for (const s of slots) {
            if (!groups.has(s.date)) groups.set(s.date, []);
            groups.get(s.date).push(s);
        }
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([date, list]) => {
            const dt = new Date(`${date}T00:00:00`);
            return {
                key: date,
                dayShort: dt.toLocaleDateString(undefined, { weekday: "short" }),
                monthDay: dt.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                count: list.length,
            };
        });
    }, [slots]);

    const currentDayKey = activeDay || dayTabs[0]?.key || null;
    const filteredSlots = useMemo(
        () => slots.filter((s) => s.date === currentDayKey).sort((a, b) => a.start_time.localeCompare(b.start_time)),
        [slots, currentDayKey],
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingSlots(true);
            try {
                const r = await axios.get(`${API}/coach/schedule/slots`, {
                    params: { from_date: weekFrom, to_date: weekTo },
                    withCredentials: true,
                });
                if (cancelled) return;
                setSlots((r.data || []).filter((s) => (s.booking_count || 0) < (s.capacity || 1)));
            } catch (e) {
                toast.error("Couldn't load slots", { description: e?.response?.data?.detail });
            } finally {
                if (!cancelled) setLoadingSlots(false);
            }
        })();
        return () => { cancelled = true; };
    }, [weekFrom, weekTo]);

    const submit = async () => {
        if (!chosenSlot) { toast.error("Pick a new slot"); return; }
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/coach/assessment-requests/${requestId}/reschedule`,
                { slot_id: chosenSlot.slot_id },
                { withCredentials: true },
            );
            toast.success("Rescheduled — parent has been emailed the new time");
            onDone?.();
        } catch (e) {
            toast.error("Couldn't reschedule", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="reschedule-slot-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Reschedule assessment</h3>
                    <button type="button" data-testid="reschedule-close" onClick={onClose} className="text-zinc-300 hover:text-white">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="text-sm text-zinc-200">
                        Moving <strong className="text-white">{parentName}</strong>
                        {currentLabel ? <span className="text-zinc-400"> · currently {currentLabel}</span> : null}
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">Pick the new slot</span>
                            <div className="flex gap-2 text-xs">
                                <button type="button" onClick={() => { setWeekStart((d) => addDays(d, -7)); setActiveDay(null); }} className="text-zinc-200 hover:text-white px-2">‹ Prev</button>
                                <button type="button" onClick={() => { setWeekStart((d) => addDays(d, 7)); setActiveDay(null); }} className="text-zinc-200 hover:text-white px-2">Next ›</button>
                            </div>
                        </div>
                        {loadingSlots ? (
                            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand-red" /></div>
                        ) : slots.length === 0 ? (
                            <p className="text-sm text-zinc-300 py-4">No open slots in this 2-week window. Check the next week or create a one-off slot.</p>
                        ) : (
                            <>
                                <div data-testid="reschedule-day-tabs" className="flex gap-1 overflow-x-auto pb-2 mb-3 -mx-1 px-1 scrollbar-thin">
                                    {dayTabs.map((d) => {
                                        const isActive = d.key === currentDayKey;
                                        return (
                                            <button
                                                key={d.key}
                                                type="button"
                                                data-testid={`reschedule-day-tab-${d.key}`}
                                                onClick={() => setActiveDay(d.key)}
                                                className={`flex-shrink-0 px-3 py-2 rounded-md text-center border transition-colors ${
                                                    isActive
                                                        ? "border-brand-red bg-brand-red/10 text-white"
                                                        : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-600"
                                                }`}
                                            >
                                                <div className="text-[10px] tracking-[0.2em] uppercase opacity-80">{d.dayShort}</div>
                                                <div className="text-sm font-display tracking-wider">{d.monthDay}</div>
                                                <div className="text-[10px] text-zinc-500 mt-0.5">{d.count} slot{d.count === 1 ? "" : "s"}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                                    {filteredSlots.map((s) => (
                                        <button
                                            key={s.slot_id}
                                            type="button"
                                            data-testid={`reschedule-pick-slot-${s.slot_id}`}
                                            onClick={() => setChosenSlot(s)}
                                            className={`text-left rounded-lg border px-3 py-2 transition ${
                                                chosenSlot?.slot_id === s.slot_id
                                                    ? "border-brand-red bg-brand-red/10"
                                                    : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                                            }`}
                                        >
                                            <div className="text-sm font-display tracking-wider text-white">{formatTimeDisplay(s.start_time)}</div>
                                            <div className="text-[10px] text-zinc-300 mt-0.5">{s.label || `${s.duration_minutes}min`} · {s.booking_count}/{s.capacity}</div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-ghost-tactical" disabled={submitting}>Cancel</button>
                        <button
                            type="button"
                            data-testid="reschedule-submit"
                            onClick={submit}
                            disabled={submitting || !chosenSlot}
                            className="btn-tactical disabled:opacity-50"
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            Move to this slot
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
